import React, { useState, useEffect } from "react";
import { useRef } from "react";
import styled from "styled-components";
import moment from "moment";
import _ from "lodash";
import { useSnackbar } from "notistack";
import queryString from "query-string";
import { navigate } from "@reach/router";
import CustomTable from "../CustomTable/CustomTable";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import {
  getErrorMessage,
  getPartyLabelByStatus,
  parseTimeStamp,
} from "../../../helpers/functions";
import PartyService from "../../../services/PartyService";
import labels from "../../../helpers/labels.json";

function handleNavigateToParty(type, id) {
  switch (type) {
    case "active":
      navigate(`/dashboard/parties/${id}`);
      break;
    default:
      navigate(`/dashboard/parties/draft/${id}`);
      break;
  }
}

async function getParties(query = "") {
  try {
    const response = await PartyService.getParties(query);
    const stats = await PartyService.partyStats();
    return { ...response, stats };
  } catch (error) {
    throw error;
  }
}

const filterByStatus = (selectedFilter) => {
  let key = "";
  switch (selectedFilter) {
    case labels["filters.draft"]:
      key = "draft";
      break;
    case labels["filters.under_verify"]:
      key = "underReview";
      break;
    case labels["filters.active"]:
      key = "active";
      break;
    default:
      break;
  }
  return key;
};

const initState = [
  { label: labels["filters.all"], key: "allCount" },
  { label: labels["filters.active"], key: "activeCount" },
  { label: labels["filters.draft"], key: "draftCount" },
  { label: labels["filters.under_verify"], key: "underReviewCount" },
];

export default function PartyTable() {
  const [state, setState] = useState();
  const MTRef = useRef();
  const [filters, setFilters] = useState(initState);
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFilter, setSelectedFilter] = useState();

  useEffect(() => {
    if (selectedFilter && MTRef?.current?.onQueryChange) {
      MTRef.current.onQueryChange({
        ...MTRef.current.state.query,
        page: 0,
      });
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (!_.isEmpty(state)) {
      setFilters([
        { label: labels["filters.all"], value: state.total },
        { label: labels["filters.draft"] },
        { label: labels["filters.under_verify"] },
      ]);
    }
  }, [state]);

  function setStats(stats) {
    const mapped = initState.map((filter) => {
      let returnData = {
        label: filter.label,
      };
      if (stats[filter.key] >= 0) {
        returnData.value = stats[filter.key];
      }
      return returnData;
    });
    setFilters(mapped);
  }

  const getPartyAgent = (agentList) => {
    if (agentList[0]?.role === "primary") {
      return "Primary Counsel";
    } else {
      return "Agent";
    }
  };

  const data = (query) =>
    new Promise((resolve) => {
      let params = {
        page: query.page + 1,
        status: filterByStatus(selectedFilter),
        perPage: 10,
        search: query.search,
      };
      if (query.orderBy?.field) {
        params.sort = query.orderBy.field;
        params.sortDirection = query.orderDirection;
      }
      let stringParams = "";
      if (!_.isEmpty(params)) {
        stringParams = `?${queryString.stringify(params)}`;
      }
      getParties(stringParams)
        .then((result) => {
          if (result) {
            resolve({
              data: result.data,
              page: result.page - 1,
              total: result.total,
            });
            setState(result);
            setStats(result.stats);
          }
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), {
            variant: "error",
          });
          setState({ data: [], lastPage: 1, page: 1, perPage: 10, total: 0 });
          resolve({
            data: [],
            page: 0,
            total: 0,
          });
        });
    });

  const columns = [
    {
      field: "id",
      title: labels["table.party_id"],
      sorting: true,
      tooltip: "Unique Identifier for the Party across IAMC platform",
      headerStyle: {
        textAlign: "left",
        width: "12%",
        paddingLeft: 16,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 16,
        paddingRight: 8,
      },
      render: (rowData) => (
        <HyperLink
          onClick={() => handleNavigateToParty(rowData?.status, rowData?.id)}
          title={rowData.id}
        >
          {rowData.id}
        </HyperLink>
      ),
    },
    {
      field: "name",
      title: labels["table.party_name"],
      sorting: false,
      headerStyle: {
        textAlign: "left",
        width: "18%",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <BoldName
          onClick={() => handleNavigateToParty(rowData?.status, rowData?.id)}
          title={rowData.name}
        >
          {rowData.name}
        </BoldName>
      ),
    },
    {
      field: "kind",
      title: labels["table.party_type"],
      sorting: false,
      tooltip: "Claimant or Respondent",
      headerStyle: {
        textAlign: "center",
        width: "12%",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "center",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <StatusContainer kind={rowData.kind}>
          {_.capitalize(rowData.kind)}
        </StatusContainer>
      ),
    },
    {
      field: "role",
      title: labels["table.party_role"],
      tooltip:
        "Your role is either Claimant or Respondent with respect to a case",
      sorting: false,
      headerStyle: {
        textAlign: "left",
        width: "15%",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <RoleContainer>
          {rowData?.agents?.length > 0 ? (
            getPartyAgent(rowData?.agents)
          ) : (
            <EmptyCell>—</EmptyCell>
          )}
        </RoleContainer>
      ),
    },
    {
      field: "documentKind",
      title: labels["table.party_idKind"],
      tooltip: "Identifier Type",
      sorting: false,
      headerStyle: {
        textAlign: "center",
        width: "13%",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "center",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <StatusContainer>
          {rowData.documentKind === "voter"
            ? "VOTER ID"
            : rowData.documentKind === "aadhar"
              ? "AADHAAR"
              : _.upperCase(rowData.documentKind)}
        </StatusContainer>
      ),
    },
    {
      field: "status",
      title: labels["table.status"],
      tooltip: "Status of the Party",
      sorting: false,
      headerStyle: {
        textAlign: "center",
        width: "14%",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "center",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <StatusContainer>
          {getPartyLabelByStatus(rowData.status)}
        </StatusContainer>
      ),
    },
    {
      field: "created_at",
      title: labels["table.created"],
      tooltip: "Party Created Date",
      sorting: false,
      headerStyle: {
        textAlign: "right",
        width: "16%",
        paddingRight: 16,
      },
      cellStyle: {
        textAlign: "right",
        paddingRight: 16,
        paddingLeft: 8,
      },
      render: (rowData) => (
        <DateText>
          {rowData?.submittedOn
            ? moment(rowData.submittedOn).format("DD/MM/YYYY")
            : moment(parseTimeStamp(rowData.created_at)).format("DD/MM/YYYY")}
        </DateText>
      ),
    },
  ];

  return (
    <CustomTable
      hidePagination={state?.lastPage === 1}
      pageSize={state?.data?.length ? state?.data?.length : 10}
      pluralTitle={labels["parties"]}
      singularTitle={labels["party"].toLowerCase()}
      placeholderText={labels["table.partySearch"]}
      {...{
        columns,
        data,
        filters,
        selectedFilter,
        setSelectedFilter,
        MTRef,
      }}
      state={state}
    />
  );
}

// const HyperLink = styled.span`
//   cursor: pointer;
//   color: ${COLORS.BTN_GREEN};
//   text-decoration: underline;
//   padding-left: 29px;
// `;

export const TableGlobalStyles = `
  .MuiPaper-root {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    border-radius: 8px !important;
    overflow: hidden;
  }

  .MuiTable-root {
    background-color: white;
  }

  thead tr {
    background-color: #f8f9fa !important;
    border-bottom: 2px solid #e1e3ee !important;
  }

  thead th {
    font-family: openSans-SemiBold, sans-serif !important;
    color: #7fa1c3 !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 14px 8px !important;
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid #e1e3ee !important;
    transition: background-color 0.2s ease;
  }

  tbody tr:hover {
    background-color: #f8fafc !important;
  }

  tbody tr:last-child {
    border-bottom: none !important;
  }

  tbody td {
    font-family: openSans, sans-serif !important;
    padding: 12px 8px !important;
    font-size: 14px;
    vertical-align: middle;
  }

  
  @media (max-width: 768px) {
    thead th {
      font-size: 11px !important;
      padding: 10px 4px !important;
    }

    tbody td {
      padding: 10px 4px !important;
      font-size: 13px;
    }

    .MuiTable-root {
      font-size: 12px;
    }
  }

 
  @media (min-width: 769px) and (max-width: 1024px) {
    thead th {
      font-size: 12px !important;
      padding: 12px 6px !important;
    }

    tbody td {
      padding: 11px 6px !important;
      font-size: 13px;
    }
  }
`;

const HyperLink = styled.a`
  color: #00838c;
  text-decoration: none;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;

  &:hover {
    color: #00838c;
    text-decoration: underline;
  }

  &:active {
    color: #00838c;
  }

  @media (max-width: 768px) {
    font-size: 13px;
    max-width: 90px;
  }
`;

const BoldName = styled.div`
  font-weight: 600;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 13px;
    max-width: 120px;
  }
`;

const RoleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DateText = styled.span`
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFont};
  font-size: 14px;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const EmptyCell = styled.span`
  color: #bdc3c7;
  font-size: 14px;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

// Optional: Mobile Responsive Card View (for very small screens)
export const ResponsiveCardContainer = styled.div`
  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 0;

    .MuiTable-root {
      display: grid;
    }

    thead {
      display: none;
    }

    tbody {
      display: grid;
      gap: 12px;
    }

    tr {
      display: grid;
      border: 1px solid #e1e3ee !important;
      border-radius: 8px;
      padding: 12px;
      background: white;

      &::before {
        content: attr(data-label);
        font-weight: 600;
        color: #7fa1c3;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
      }
    }

    td {
      padding: 8px 0 !important;
      display: block;
      text-align: left !important;
    }
  }
`;
